import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { useMemo } from "react";

import { DEFAULT_BACKEND_URL } from "../config/CONSTANTS";

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_BACKEND_URL ?? DEFAULT_BACKEND_URL}`,
  withCredentials: true,
});

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

const isOnAuthPage = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  const authPages = [
    "/login",
    "/signup",
    "/forgot-password",
    "/reset-password",
    "/accept-organisation-invitation",
  ];

  return authPages.some((page) => window.location.pathname.startsWith(page));
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/auth/login")
    ) {
      if (isOnAuthPage()) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = axiosInstance
          .post("/auth/refresh")
          .then(() => {
            isRefreshing = false;
            refreshPromise = null;
            return true;
          })
          .catch(() => {
            isRefreshing = false;
            refreshPromise = null;
            if (typeof window !== "undefined" && !isOnAuthPage()) {
              window.location.href = "/login";
            }
            return false;
          });
      }

      const currentRefreshPromise = refreshPromise;
      if (currentRefreshPromise) {
        const refreshSucceeded = await currentRefreshPromise;
        if (refreshSucceeded) {
          return axiosInstance(originalRequest);
        }
      }
    }

    return Promise.reject(error);
  },
);

export const useAxios = (): { axiosInstance: AxiosInstance } => {
  return useMemo(() => ({ axiosInstance }), []);
};
